import { faSitemap } from "@fortawesome/pro-regular-svg-icons"
import { faSearch } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Checkbox,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material"
import { translate } from "app/language/service"
import { getActiveAppContext } from "app/tools/common"
import { formEventModule } from "form/event"
import { FORM_TAB_BOXES, IForm } from "form/model"
import { noticeAndClearOperatorError, refreshOperatorList, setOperatorFilters } from "form/tools/operator"
import { noticeAndClearSspError, synchronizeNewSspSelection } from "form/tools/ssp"
import React from "react"
import { getAllSspsInStore } from "ssp/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { SelectionFilter } from "../filter/SelectionFilter"
import { ScreensBoxLayout } from "./ScreensBoxLayout"
import { SspOperatorRow } from "./SspOperatorRow"

interface IContentSspAndOperatorProps {
  form: IForm
}

export const ContentSspAndOperator = React.memo(({ form }: IContentSspAndOperatorProps): JSX.Element => {
  const isFirstRender = React.useRef(true)
  const [rowRenderIndex, setRowRenderIndex] = React.useState(0)
  const [selectedSspIds, setSelectedSspIds] = React.useState<number[]>(form.screens.sspIds)
  const isNoticingSspError = React.useRef(false)
  const isNoticingOperatorError = React.useRef(false)
  const sspError = form.context.screens.errors.sspIds
  const isDspDealForm = form.context.isDspDeal
  React.useEffect(() => {
    if (!getActiveAppContext()) {
      return
    }
    if (form.context.relations.sspsError) {
      if (!isNoticingSspError.current) {
        isNoticingSspError.current = true
        noticeAndClearSspError(form)
        setSelectedSspIds(form.screens.sspIds)
      }
    } else if (isNoticingSspError.current) {
      isNoticingSspError.current = false
    }
    if (form.context.relations.operatorsError) {
      if (!isNoticingOperatorError.current) {
        isNoticingOperatorError.current = true
        noticeAndClearOperatorError(form)
        setRowRenderIndex(rowRenderIndex + 1)
      }
    } else if (isNoticingOperatorError.current) {
      isNoticingOperatorError.current = false
    }
    if (isFirstRender.current) {
      isFirstRender.current = false
      refreshOperatorList(form.context.appContext)
    }
    formEventModule.on.environmentsSelectionChanged("component-ContentSspAndOperator", () =>
      refreshOperatorList(form.context.appContext, form.screens.sspIds)
    )
    return () => formEventModule.remove("component-ContentSspAndOperator")
  })
  const allSsps = getAllSspsInStore()
  const selectedSspCount = selectedSspIds.length
  const areAllSspSelected = allSsps.length === selectedSspCount
  const isSynchronizing = form.screens.isSynchronizingSsp || form.screens.isSynchronizingOperator
  const descriptions = isDspDealForm
    ? [translate("ssp.and.operator.description2")]
    : [translate("ssp.and.operator.description1"), translate("ssp.and.operator.description2")]
  return (
    <ScreensBoxLayout
      id={FORM_TAB_BOXES.SCREENS_SSP}
      title={translate("ssp.and.operator.title")}
      isLoading={isSynchronizing}
      icon={faSitemap}
      sxChildren={{
        "& > *": {
          width: "100%"
        },
        "& .MuiTableCell-root": {
          fontWeight: 300,
          fontSize: ".85rem"
        },
        "& .MuiTableCell-head, & .MuiTableCell-root": {
          pt: ".4rem",
          pb: ".4rem"
        }
      }}>
      <FlexBox jc="space-between" wrap gap>
        <FlexBox vertical ai="start" jc="start">
          {descriptions.map((description, index) => (
            <Typography id="screen-ssp-description" color="textSecondary" fontSize=".85rem" key={index}>
              {description}
            </Typography>
          ))}
        </FlexBox>
        <FlexBox>
          <FlexBox jc="start" wrap gap>
            <TextField
              id="screen-ssp-find"
              label={translate("label.find.operator")}
              value={form.screens.operatorFilter}
              onChange={event => setOperatorFilters({ name: event.target.value })}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faSearch} size="lg" />
                  </InputAdornment>
                )
              }}
            />
            <SelectionFilter
              id="screen-ssp-selection-filter"
              value={form.screens.operatorSelectFilter}
              onChange={newSelectFilter => setOperatorFilters({ selectFilter: newSelectFilter })}
            />
          </FlexBox>
        </FlexBox>
      </FlexBox>
      {sspError && (
        <Typography id="screen-ssp-error" color="error" fontSize=".9rem" minHeight="1.5rem" sx={{ mt: "1rem" }}>
          {translate("ssp.error")}
        </Typography>
      )}
      <TableContainer sx={{ mt: "1rem" }}>
        <Table aria-label="collapsible table" sx={{ border: theme => theme.palette.border, borderBottom: "none" }}>
          <TableHead>
            <TableRow>
              <TableCell width="1rem">
                <Checkbox
                  id="screen-ssp-select-all"
                  disabled={isDspDealForm || form.screens.isSynchronizingOperator}
                  checked={areAllSspSelected}
                  indeterminate={selectedSspCount !== 0 && !areAllSspSelected}
                  onChange={(_event, checked) => {
                    const newSspSelectedIds = checked ? allSsps.map(ssp => ssp.id) : []
                    synchronizeNewSspSelection(newSspSelectedIds)
                    setSelectedSspIds(newSspSelectedIds)
                  }}
                />
              </TableCell>
              <TableCell>
                <FlexBox jc="start" gap>
                  <Typography id="screen-ssp-list-title" color="textPrimary" fontSize=".95rem" fontWeight={600}>
                    {translate("ssp.list")}
                  </Typography>
                  <Typography
                    id="screen-ssp-list-length"
                    color="textSecondary"
                    fontSize=".9rem">{`${selectedSspCount} / ${allSsps.length}`}</Typography>
                </FlexBox>
              </TableCell>
              <TableCell width="1rem" />
            </TableRow>
          </TableHead>
          <TableBody>
            {allSsps.map((ssp, index) => (
              <SspOperatorRow
                key={ssp.id}
                index={index}
                form={form}
                ssp={ssp}
                rowRenderIndex={rowRenderIndex}
                disabled={isDspDealForm || form.screens.isSynchronizingOperator}
                isSelected={selectedSspIds.includes(ssp.id)}
                setSelectedSspIds={newSelectedSspIds => setSelectedSspIds(newSelectedSspIds)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ScreensBoxLayout>
  )
})
